import { showErrorToast } from "src/components/AlertToastify/AlertToastify";
import { fetchWithCache } from "src/utils/cacheUtils";
import { trackEndpointAccess, trackEndpointResponseTime } from "src/utils/analyticsCapFirebase";
import { resolveUnsyncedPocIntegrationName } from "src/constants/unsyncedLeague";

interface ErrorResponse {
  detail?: string;
  message?: string;
}

export const getNews = async (
  authId,
  userDataPlatform,
  leagueIdPlatform,
  teamName: string,
  pocToken,
  accessAuthToken,
  signal?: AbortSignal,
  yahooToken?: string,
  forceRefresh = false,
  calculatorSettings?: any,
  /** Needed to safely map null → "un_synced" only for Un-Synced */
  platformId?: string | null,
): Promise<any> => {
  try {
    const startedAt = Date.now();
    const url = `${process.env.REACT_APP_BACKEND_URL}/functions/fantasy_league_news`;
    //cache key for news
    const cacheKey = `news-${leagueIdPlatform}-${userDataPlatform.user_id}`;

    const requestData: any = {
      league_integration: {
        pocToken: pocToken,
        leagueToken: userDataPlatform.integration_name === "yahoo" ? yahooToken : userDataPlatform.token,
        user_id: userDataPlatform.user_id,
        league_id: leagueIdPlatform,
        // Un-Synced: POC enum requires "un_synced" (guarded by platformId)
        integration_name: resolveUnsyncedPocIntegrationName(userDataPlatform.integration_name, platformId),
        force_refresh: forceRefresh,
        // common_data: commonLeagueData,
      },
      //   response_settings: {
      //     persona_input: "standard",
      //     max_words: 0,
      //   },
      sport: "NFL",
      //   model: "RotoGPT 4.0",
      // Add calculator_settings if provided (from LeagueSettings component)
      ...(calculatorSettings && { calculator_settings: calculatorSettings }),
    };

    // If the platform is ESPN, add the specific fields it requires
    if (userDataPlatform.integration_name === "espn") {
      requestData.league_integration.swid = userDataPlatform.swid;
      requestData.league_integration.espn_s2 = userDataPlatform.s2;
    }

    // console.log("news REQUEST DATA", requestData);

    const options: RequestInit = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessAuthToken}`,
        "X-Skip-Cache": "true",
      },
      body: JSON.stringify(requestData),
      signal,
      credentials: "include",
    };

    const { data, fromCache, ok } = await fetchWithCache(url, options, leagueIdPlatform, userDataPlatform.user_id, authId, forceRefresh);

    // --- TRACK EVENT ON REAL EXECUTION (NOT CACHE) ---
    if (!fromCache && ok) {
      void trackEndpointAccess(authId, userDataPlatform.integration_name, leagueIdPlatform, teamName, "news");
      void trackEndpointResponseTime(authId, "news", (Date.now() - startedAt) / 1000, userDataPlatform.integration_name, leagueIdPlatform);
    }

    // if (fromCache) {
    //   console.log(`[News Cache] HIT for: ${cacheKey}`);
    // } else {
    //   console.log(`[News Cache] MISS for: ${cacheKey}`);
    // }

    if (!ok) {
      const errorData = data as ErrorResponse & { statusCode?: number };
      // Detect rate limit via response body (fetchWithCache doesn't expose HTTP status)
      if (errorData?.statusCode === 429 || errorData?.message === "TOO_MANY_REQUESTS") {
        const err = new Error("TOO_MANY_REQUESTS") as any;
        err.isRateLimit = true;
        err.retryAfter = (errorData as any)?.retryAfter ?? 60;
        throw err;
      }
      throw new Error(errorData.detail || errorData.message);
    }

    return data;
  } catch (error) {
    if (error.name === "AbortError") {
      // Handle abort silently
      console.log("Request was aborted");
      return null;
    }
    // Rate limit: show specific message without exposing the raw error code
    if ((error as any).isRateLimit) {
      showErrorToast("You're making too many requests. Please slow down and try again in a moment.");
      throw error;
    }
    // console.error("Something went wrong when getting news data", error);
    // showErrorToast("Something went wrong when getting news data");
    // throw error;
    console.error("Error in getNews:", error);
    showErrorToast(error.message);
    throw error;
  }
};
